import { BlogAndSubscribeDetails } from "../types"
import { getModalPath, ModalRoute } from "../utils"
import { subscribeUserToBlog } from "@/lib/api/blogs"
import { isErrorResponse } from "@types"
import { captureException } from "@sentry/nextjs"
import { subscribeError } from "../tracking"
import { isUserLoggedIn } from "@/lib/utils/user"

/**
 * Handles the completion of the enter email form.
 *
 * When the user completes the enter email form, we need to redirect them to the
 * next page. That next page is either the create account page or the login page,
 * depending on whether the user already exists in our database or not.
 *
 * This needs to be in a client component, otherwise we get the error:
 * "Event handlers cannot be passed to Client Component props."
 *
 * @param blogDetails - The blog details.
 * @returns The next redirect path.
 */
export async function onComplete(
  blogDetails: BlogAndSubscribeDetails,
  email: string
): Promise<string> {
  const [isLoggedIn, resp] = await Promise.all([
    isUserLoggedIn(),
    // Subscribe user to the blog immediately.
    // TODO: Add referrer info.
    subscribeUserToBlog({
      lowercaseBlogUrl: blogDetails.lowercase_url,
      blogId: blogDetails.id,
      email,
    }),
  ])

  const isError = isErrorResponse(resp)

  const alreadySubscribed = isError && resp.code === "ALREADY_SUBSCRIBED"

  if (alreadySubscribed && isLoggedIn) {
    // If the user is already subscribed to the blog and logged in, redirect them to the already subscribed page.
    console.log(
      "User is already subscribed to blog, continuing to already subscribed page.",
      resp
    )

    return getModalPath(ModalRoute.ALREADY_SUBSCRIBED, blogDetails)
  } else if (alreadySubscribed && !isLoggedIn) {
    // If the user is already subscribed to the blog and not logged in, redirect them to the login page.
    console.log(
      "User is already subscribed to blog, but not logged in, redirecting to login page.",
      resp
    )

    const loginSearchParams = new URLSearchParams({ email }).toString()

    return `${getModalPath(ModalRoute.LOGIN, blogDetails)}?${loginSearchParams}`
  } else if (isError) {
    // If there was any other error subscribing the user to the blog, log an error and return.
    console.error("Error subscribing user to blog!", resp)

    // Track the error on Amplitude.
    subscribeError(blogDetails?.id || "", resp.msg)

    // Log to Sentry.
    const err = new Error("Error subscribing user to blog!")
    captureException(err)

    throw err
  }

  const nextState = resp && "nextState" in resp && resp?.nextState

  console.log("EnterEmail onComplete nextState", nextState)

  switch (nextState) {
    case "SET_PASSWORD":
      // Since the user has already filled out their email, let's redirect them to the account creation page,
      // where they will see a prefilled email field, and can set their password.
      const createAccountSearchParams = new URLSearchParams({
        email,
      }).toString()

      // If the user either didn't have an account, or had an account that was created as part of a subscriber import, and
      // therefore did not have a password, either way they now have a partially created account in our database, and we need to
      // redirect them to the account creation page.
      return `${getModalPath(ModalRoute.CREATE_ACCOUNT, blogDetails)}?${createAccountSearchParams}`

    case "LOGIN":
      // Since the user has already filled out their email, let's redirect them to the login page,
      // where they will see a prefilled email field, and can enter their password.
      const loginSearchParams = new URLSearchParams({ email }).toString()

      // Redirect to login page.
      return `${getModalPath(ModalRoute.LOGIN, blogDetails)}?${loginSearchParams}`

    case "EMAIL_ENTRY":
      // This should never happen since this page already entered their email. If this does happen
      // let's log an error here.
      const errorMessage =
        "Unexpected next state! User should not be redirected to email entry page after having just entered their email."

      console.error(errorMessage)

      // Log to Sentry.
      const err = new Error(errorMessage)
      captureException(err)

      throw err

    case "WALLET_ENTRY":
      // If the back-end wants them to connect their wallet, let's redirect them
      // to the communities page since that means they're already logged in with an email
      // and they'll get another chance to connect their wallet on the last page of the subscribe flow.
      return getModalPath(ModalRoute.CHOOSE_COMMUNITIES, blogDetails)

    default:
      // This should never happen.
      const defaultErrorMessage =
        "Unexpected next state! Unknown state: " + nextState

      console.error(defaultErrorMessage)

      // Track the error on Amplitude.
      subscribeError(blogDetails?.id || "", defaultErrorMessage)

      // Log to Sentry.
      const defaultErr = new Error(defaultErrorMessage)
      captureException(defaultErr)

      throw defaultErr
  }
}
